import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AnalyticsConfigService } from '@core/services/core/analytics/analytics-configuration';
import { AnalyticsContext } from '@core/services/core/analytics/analytics-context';
import { FirebaseX } from '@ionic-native/firebase-x/ngx';
import { Platform } from '@ionic/angular';
import { AnalyticsInterface } from '@models/analytics';
import { PageAnalytics } from '@models/page-analytics';
import { addBreadcrumb } from '@sentry/minimal';
import { Severity } from '@sentry/types';
import 'firebase/analytics';
import { filter, map, mergeMap } from 'rxjs/operators';
import { ContextService } from '../context.service';
import { AnalyticsClicks } from './analytics-clicks';
import { AnalyticsNavigation } from './analytics-navigation';

export interface Config {
  isDisabled?: boolean;
  debug?: ('screenview' | 'click')[];
  verbose?: boolean;
  labelOverride?: Map<string, string>;
  action?: Map<string, string>;
  category?: Map<string, string>;
  excludeTags?: string[];
  googleAnalyticsCookie?: string;
  pageType?: [string, string][];
}

const MAX_GA4_VALUE_SIZE = 100;
@Injectable({
  providedIn: 'root'
})
export class AnalyticsInitializer {

  private document = document;

  constructor(
    private firebasex: FirebaseX,
    private platform: Platform,
    private ctx: ContextService,
    private router: Router,
    private route: ActivatedRoute,
    private analyticsClicks: AnalyticsClicks,
    private analyticsNavigation: AnalyticsNavigation,
    private analyticsConfigService: AnalyticsConfigService,
    private analyticsCtx: AnalyticsContext
  ) {}

  get config() {
    return this.analyticsConfigService.getConfig();
  }

  init(config: Config) {
    this.analyticsConfigService.setConfig(config);

    if (this.config.isDisabled) return;

    if (!this.platform.is('hybrid')) {
      this.addScriptTagGTM();
    }
    this.listenToRouteChanges();
    this.bindNavigationEvents();
    this.bindClickEvents();
  }

  /**
   * @description Not recommended, try to rely on automatic analytics feature
   */
  async triggerManualNavigation(url? ,newParams?:Partial<AnalyticsInterface>) {
    const params = (await this.analyticsNavigation.getNavigationParams(url)) || {};
    if (params.disabled) return;
    this.track('screenview',  {...params, ...newParams});
  }

  async triggerManualClick(category: string, action: string, label: string, newParams?: any) {
    const params = (await this.analyticsClicks.getCustomClickParams(category, action, label)) || {};
    if (params.disabled) return;
    this.track('click', {...params, ...newParams});
  }

  private listenToRouteChanges() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map((navigation: NavigationEnd) => ({
          route: this.route,
          url: navigation.urlAfterRedirects || navigation.url
        })),
        map(({ route, url }) => {
          while (route.firstChild) route = route.firstChild;
          return { route, url };
        }),
        filter(({ route }) => route.outlet === 'primary'),
        mergeMap(({ route, url }) =>
          route.data.pipe(map(data => ({ url, data })))
        )
      )
      .subscribe(({ url, data }) => {
        const { analytics }: { analytics: PageAnalytics } = data || ({} as any);

        this.analyticsCtx.setPageTitle({
          url,
          slug: analytics && analytics.slug
        });
      });
  }

  private bindNavigationEvents() {
    this.ctx.onPageChange$().subscribe(async url => {
      if (this.platform.is('hybrid')) this.firebasex.setScreenName(url);
      const params: any =
        (await this.analyticsNavigation.getNavigationParams(url)) || {};
      if (params.disabled) return;
      this.track('screenview', params);
    });
  }

  private bindClickEvents() {
    document.body.addEventListener(
      'click',
      async ev => {
        const params: any =
          (await this.analyticsClicks.getClickParams(ev)) || {};
        if (params.disabled) return;
        this.track('click', params);
      },
      true
    );
  }

  private track(eventName: 'click' | 'screenview' | string, params: object) {
    this.log({ eventName, params });
    for (const key in params) {
      if (typeof params[key] === 'string') {
        params[key] = params[key].substring(0, MAX_GA4_VALUE_SIZE);
      }
    }
    if (this.platform.is('hybrid')) {
      this.firebasex.logEvent(eventName, params);
    } else {
      this.setWebDataLayer(params);
      if (eventName === 'screenview') {
        (window as any).sendPageViewMETS?.(params);
      } else {
        (window as any).sendEventMETS?.(eventName, params['action'], params['label']);
      }
    }
  }

  private log(obj: { eventName; params }) {
    const msg = this.buildMsg(obj);
    this.logDebug(msg);
    this.logSentry(msg);
  }

  private buildMsg({ eventName, params }) {
    const category = eventName === 'click' ? 'action' : 'navigation';
    const categoryStyle =
      eventName === 'click'
        ? 'color:white; background-color: #68b631;'
        : 'color:white; background-color: #315aaf;';
    const subtitleStyle = 'color: inherit;';
    const subtitle = eventName === 'click' ? params.label : params.Pagetitle;
    const message = `%c ${category} %c ${subtitle}`;
    const extra =
      (this.config.verbose && JSON.stringify(params, null, '\t')) || '';

    return {
      message,
      extra,
      categoryStyle,
      subtitleStyle,
      eventName,
      category,
      subtitle
    };
  }

  private logDebug({
    message,
    extra,
    categoryStyle,
    subtitleStyle,
    eventName
  }) {
    if (this.config.debug.includes(eventName)) {
      console.log(message + extra, categoryStyle, subtitleStyle);
    }
  }

  private logSentry({ category, subtitle }) {
    if (category === 'action') {
      const sentryMessage = `${subtitle}`;
      addBreadcrumb({
        category,
        message: sentryMessage,
        level: Severity.Log
      });
    }
  }

  private setWebDataLayer(data: any) {
    (window as any).digitalData = data;
  }

  private addScriptTagGTM() {
    if (!this.document.getElementById('gtm_script')) {
      // Initialize web channel dataLayer
      this.setWebDataLayer({});
      // Inject GTM
      const script: any = this.document.createElement('script');
      script.id = 'gtm_script';
      script.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-MCC7492');`;
      script.defer = true;
      this.document.head.appendChild(script);
    }
  }
}
